import style from "./CourseCard.module.css";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { padZero, secondsToHoursMinutes } from "@/utils/helpers";
import CourseCategory from "../courseCategory/CourseCategory";
import Icon from "@/ui/icon/Icon";
import { successToast } from "@/ui/toast/Toast";
import { useSaveCourse } from "../../hooks/useSaveCourse";
import { useUnsaveCourse } from "../../hooks/useUnsaveCourse";

import { useUser } from "@/features/auth/hooks/useUser";
import { useLocalized } from "@/i18n/useLocalized";

function CourseCard({ course, showWatchList = false }) {
	const { t } = useTranslation();
	const l = useLocalized();
	const {
		thumbnail,
		slug,
		like_count: likeCount,
		module_count: moduleCount,
		duration,
	} = course;

	const { saveCourse } = useSaveCourse({ course });
	const { unsaveCourse } = useUnsaveCourse({ course });

	const { user, isLoggedIn } = useUser();

	const handleAddToWatchListClick = async (e) => {
		e.preventDefault();
		try {
			// i18n.t, not t: the request may outlast a language switch, and the
			// toast should come out in the language current when it shows.
			if (course?.is_saved) {
				await unsaveCourse();
				successToast(i18n.t("courses:card.removed"));
			} else {
				await saveCourse();
				successToast(i18n.t("courses:card.added"));
			}
		} catch (e) {
			console.log("Save course error", e);
		}
	};

	// Compute course card classes based on state
	const isLocked = isLoggedIn && !course?.is_free && !user?.is_subscribed;
	const isCompleted = course?.is_completed && user?.is_subscribed;

	const courseClasses = [
		style.course,
		isLocked && style.locked,
		isCompleted && style.completed,
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div className={courseClasses}>
			<Link
				to={`/courses/${slug}`}
				className={style.thumbnail}
				style={{
					"--image": `url(${thumbnail})`,
				}}
			>
				<CourseCategory course={course} className={style.category} />
				{isLocked ? (
					<div className={style.status}>
						<div className={style.icoHolder}>
							<Icon icon="fa-lock" />
						</div>
					</div>
				) : isCompleted && !showWatchList ? (
					<div className={style.status}>
						<div className={style.icoHolder}>
							<Icon icon="fa-check" iconType="fa-solid" />
						</div>
					</div>
				) : (
					<div
						className={`${style.addToWatchlist} ${
							course?.is_saved ? style.active : ""
						} ${!isLoggedIn && style.disabled}`}
						onClick={!isLoggedIn ? null : handleAddToWatchListClick}
					>
						<Icon icon="fa-bookmark" />
					</div>
				)}
			</Link>
			<div className={style.info}>
				<div className={style.header}>
					<Link to={`/courses/${slug}`} className={style.title}>
						{l(course, "name")}
					</Link>
					{course?.lecturers?.length > 0 &&
					course?.lecturers?.length == 1 ? (
						<div className={style.author}>
							<img
								src={course.lecturers[0]?.picture_url}
								alt={course.lecturers[0]?.name}
							/>
							<div>{course.lecturers[0]?.name}</div>
						</div>
					) : (
						<div className={style.authors}>
							<div className={style.imgHolder}>
								{course.lecturers?.map((lecturer, index) => (
									<img
										key={`${lecturer.picture_url}-${index}`}
										src={lecturer.picture_url}
										alt={lecturer.name}
									/>
								))}
							</div>
							<div className={style.namesHolder}>
								{course.lecturers?.map((lecturer, index) => (
									<div key={`${lecturer.name}-${index}`}>
										<div title={lecturer.name}>
											{lecturer.name}
										</div>
										<span className={style.sep}></span>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
				<div className={style.footer}>
					<div className={style.otherInfo}>
						<div>
							<Icon icon="fa-heart" />
							<span>{padZero(likeCount)}</span>
						</div>
						<span className={style.sep}></span>
						<div>
							<Icon icon="fa-layer-group" />
							<span>{padZero(moduleCount)}</span>
						</div>
						<span className={style.sep}></span>
						<div>
							<Icon icon="fa-timer" />
							<span>{secondsToHoursMinutes(duration)}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default CourseCard;
